import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Users } from "lucide-react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Checkbox } from "@/components/Base/Checkbox";
import { useAdminReassignShopOwnerMutation } from "@/hooks/mutations/useAdminReassignShopOwnerMutation";
import { useToast } from "@/hooks/useToast";
import { sentry } from "@/services/sentry";
import {
  AdminReassignShopOwnerResult,
  PreviousOwnerHandling,
} from "@/types/v2";

const formSchema = z.object({
  newOwnerEmail: z.string().email("Enter a valid email"),
  previousOwner: z.enum(["remove", "viewOnly", "keep"]),
  sendLoginEmail: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const PREVIOUS_OWNER_OPTIONS: {
  value: PreviousOwnerHandling;
  label: string;
  description: string;
}[] = [
  {
    value: "remove",
    label: "Remove from shop",
    description:
      "They lose all access to this shop. Other shops are untouched.",
  },
  {
    value: "viewOnly",
    label: "Demote to view-only",
    description: "They stay on the shop but can no longer change anything.",
  },
  {
    value: "keep",
    label: "Leave as-is",
    description: "Their access is not changed (e.g. adding a co-owner).",
  },
];

const describeResult = (
  result: AdminReassignShopOwnerResult,
  previousOwner: PreviousOwnerHandling
) => {
  const parts: string[] = [];
  if (result.emailSent) {
    parts.push("A login email was sent to the new owner.");
  }
  if (previousOwner !== "keep" && !result.previousOwnerUpdated) {
    parts.push("No previous-owner admin was found to update.");
  }
  if (result.legacyAccessRetained) {
    parts.push(
      "Note: the previous owner keeps access through this shop's legacy ID, which can't be revoked here."
    );
  }
  parts.push(
    "Access changes apply the next time each user's session refreshes (up to about an hour)."
  );
  return parts.join(" ");
};

export const ReassignOwnerDialog = ({
  shopId,
  shopName,
  currentOwnerEmail,
}: {
  shopId: string;
  shopName: string;
  currentOwnerEmail: string;
}) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { mutateAsync, isLoading } = useAdminReassignShopOwnerMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newOwnerEmail: "",
      previousOwner: "remove",
      sendLoginEmail: true,
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await mutateAsync({
        shopId,
        newOwnerEmail: values.newOwnerEmail,
        previousOwner: values.previousOwner,
        sendLoginEmail: values.sendLoginEmail,
        loginLink: `${window.location.origin}/login`,
      });

      toast({
        title: "Shop owner reassigned",
        description: describeResult(result, values.previousOwner),
        variant: "success",
      });

      form.reset();
      setOpen(false);
    } catch (error) {
      sentry.captureException(error);
      toast({
        title: "Unable to reassign owner",
        description:
          error instanceof Error
            ? error.message
            : "Try again in a few minutes or contact support.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Users className="mr-2 h-4 w-4" />
          Reassign owner
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Reassign owner of {shopName}</DialogTitle>
        <DialogDescription>
          Current owner:{" "}
          <span className="font-medium text-foreground">
            {currentOwnerEmail}
          </span>
        </DialogDescription>
        <Form {...form}>
          {/* noValidate: let zod own email validation. Otherwise the browser's
              native type="email" constraint check blocks submit first (and jsdom
              does the same in tests), so "Enter a valid email" never renders. */}
          <form
            noValidate
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="newOwnerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="newOwnerEmail">New owner email</FormLabel>
                  <FormControl>
                    <Input
                      id="newOwnerEmail"
                      type="email"
                      placeholder="owner@example.com"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="previousOwner"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Previous owner</FormLabel>
                  <div
                    role="radiogroup"
                    aria-label="Previous owner"
                    className="flex flex-col gap-2"
                  >
                    {PREVIOUS_OWNER_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-start gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value={option.value}
                          checked={field.value === option.value}
                          onChange={() => field.onChange(option.value)}
                          disabled={isLoading}
                          className="mt-1"
                        />
                        <span>
                          {option.label}
                          <span className="block text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sendLoginEmail"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onClick={() => field.onChange(!field.value)}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormLabel>
                    Send login email now (only if the new owner has no account
                    yet)
                  </FormLabel>
                </FormItem>
              )}
            />
            <div className="flex flex-row justify-end gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="accent" disabled={isLoading}>
                {isLoading ? "Reassigning..." : "Reassign"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
